import React from 'react';
import { useSidebar } from '@ui/sidebar';

interface ClearOfferLogoProps {
  standalone?: boolean;
}

// Real brand assets (staged under core/static, served at STATIC_URL "code/static/").
// Matches the existing `/code/static/core/favicon/*` pattern used elsewhere in the app.
// The wordmark PNG already contains the word "clearoffer", so the lockup is simply the
// aperture icon + the wordmark image side by side (no additional text).
const WORDMARK_SRC = '/code/static/core/favicon/clearoffer-wordmark.png';
const ICON_SRC = '/code/static/core/favicon/clearoffer-icon.png';

const ClearOfferOriginalNoTagline: React.FC<ClearOfferLogoProps> = ({ standalone = false }) => {
  let isSidebarContext = true;
  try {
    // We only need to know whether we're inside a SidebarProvider. The collapsed
    // vs expanded state is handled purely through the sidebar's `data-collapsible`
    // group attribute (via CSS) so the logo animates in lock-step with the sidebar
    // width transition instead of being swapped out by a React re-render.
    useSidebar();
  } catch {
    // Rendered outside a SidebarProvider (e.g. login/signup screens).
    isSidebarContext = false;
  }

  const useStandaloneMode = standalone || !isSidebarContext;

  // Standalone (login/signup): full aperture + wordmark lockup, centered.
  if (useStandaloneMode) {
    return (
      <div
        className="flex w-full items-center justify-center gap-2 py-2"
        data-testid="clear-offer-logo"
      >
        <img src={ICON_SRC} alt="" className="h-8 w-8 shrink-0" data-testid="logo-icon" />
        <img
          src={WORDMARK_SRC}
          alt="ClearOffer"
          className="h-6 w-auto shrink-0"
          data-testid="logo-text"
        />
      </div>
    );
  }

  // In the sidebar: aperture icon + wordmark lockup that matches the marketing landing.
  // Expanded  → icon + wordmark side by side.
  // Collapsed → the wordmark is hidden and the icon re-centers, driven entirely by the
  //             `group-data-[collapsible=icon]` attribute so the DOM stays stable across
  //             the toggle (no element swap, no overflow past the collapsed icon rail).
  return (
    <div
      className="flex h-8 items-center gap-2 overflow-hidden group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0"
      data-testid="clear-offer-logo"
    >
      <img src={ICON_SRC} alt="" className="h-8 w-8 shrink-0" data-testid="logo-icon" />
      <img
        src={WORDMARK_SRC}
        alt="ClearOffer"
        className="h-6 w-auto shrink-0 group-data-[collapsible=icon]:hidden"
        data-testid="logo-text"
      />
    </div>
  );
};

export default ClearOfferOriginalNoTagline;
